import { register, run } from "./registry";
import { focusField } from "./keyboard";

/** 06 §4: after save → "Print? (Y/n)", Enter/Y prints, N/Esc skips; then reset keeping data-kit-carry fields. */
function resetForm(form: HTMLFormElement): void {
  form.querySelectorAll<HTMLInputElement>("input[data-kit-order]:not([data-kit-carry]), textarea[data-kit-order]:not([data-kit-carry])")
    .forEach((i) => {
      if (i instanceof HTMLInputElement && i.type === "checkbox") i.checked = i.defaultChecked;
      else i.value = "";
      if (i.dataset.kitAcTarget) {
        const picked = form.querySelector<HTMLInputElement>(`input[type='hidden'][name='${i.dataset.kitAcTarget}']`);
        if (picked) picked.value = "";
      }
    });
  form.querySelector<HTMLInputElement>("input[name='__IdempotencyKey']")?.setAttribute("value", crypto.randomUUID());
  form.querySelectorAll<HTMLTableElement>("[data-kit-grid]").forEach((grid) => {
    const tbody = grid.querySelector("tbody");
    const tpl = grid.querySelector<HTMLTemplateElement>("template[data-kit-row]");
    if (tbody) {
      tbody.replaceChildren();
      if (tpl && tpl.content.firstElementChild) {
        const clone = tpl.content.firstElementChild.cloneNode(true) as HTMLTableRowElement;
        clone.innerHTML = clone.innerHTML.replace(/\[#\]/g, "[0]");
        tbody.appendChild(clone);
        run(clone);
      }
    }
  });
  // Restore read-only panels from their empty template here, synchronously, so a fast next lookup cannot race a server reset.
  form.querySelectorAll<HTMLElement>("[data-kit-reset-to]").forEach((el) => {
    const tpl = document.getElementById(el.dataset.kitResetTo ?? "");
    if (tpl instanceof HTMLTemplateElement) el.replaceChildren(tpl.content.cloneNode(true));
  });
  delete form.dataset.kitDirty;
  form.dispatchEvent(new CustomEvent("kit:saved"));
  const first = Array.from(form.querySelectorAll<HTMLElement>("[data-kit-order]:not([data-kit-carry])"))
    .sort((a, b) => Number(a.dataset.kitOrder) - Number(b.dataset.kitOrder))[0];
  focusField(first);
}

let bound = false;
register(() => {
  if (bound) return;
  bound = true;

  // Create modal dialog element
  const dlg = document.createElement("dialog");
  dlg.id = "kit-modal";
  dlg.className = "kit-modal";
  dlg.innerHTML = `<div id="print-prompt"><p id="kit-modal-text">Print? (Y/n)</p></div>`;
  dlg.setAttribute("role", "alertdialog");
  dlg.setAttribute("aria-labelledby", "kit-modal-text");
  document.body.appendChild(dlg);

  let pendingUrl = "";

  document.body.addEventListener("confirm", (e: Event) => {
    const detail = (e as CustomEvent<{ text: string; resubmit?: Record<string, string> }>).detail;
    const p = dlg.querySelector<HTMLElement>("#kit-modal-text");
    if (p) p.textContent = detail.text;
    const form = document.querySelector<HTMLFormElement>("form[data-kit-form]");
    dlg.showModal();

    const handler = (ke: KeyboardEvent) => {
      if (ke.key === "y" || ke.key === "Y" || ke.key === "Enter") {
        ke.preventDefault();
        dlg.close();
        dlg.removeEventListener("keydown", handler);
        if (detail.resubmit && form) {
          for (const [k, v] of Object.entries(detail.resubmit)) {
            const hidden = form.querySelector<HTMLInputElement>(`input[name='${k}']`);
            if (hidden) hidden.value = v;
          }
          if ((window as any).htmx) {
            const swap = form.getAttribute("hx-swap") || "none";
            const targetAttr = form.getAttribute("hx-target");
            const target = targetAttr ? (targetAttr === "this" ? form : (targetAttr === "body" ? document.body : document.querySelector(targetAttr) || form)) : form;
            const select = form.getAttribute("hx-select") || undefined;
            (window as any).htmx.ajax("POST", form.getAttribute("hx-post") || window.location.href, { source: form, target, swap, select, values: (window as any).htmx.values(form) });
          } else {
            form.requestSubmit();
          }
        }
      } else if (ke.key === "n" || ke.key === "N" || ke.key === "Escape") {
        ke.preventDefault();
        dlg.close();
        dlg.removeEventListener("keydown", handler);
      }
    };
    dlg.addEventListener("keydown", handler);
  });

  document.body.addEventListener("printPrompt", (e: Event) => {
    const { url } = (e as CustomEvent<{ url: string }>).detail;
    pendingUrl = url;
    const p = dlg.querySelector<HTMLElement>("#kit-modal-text");
    if (p) p.textContent = "Print? (Y/n)";
    const form = document.querySelector<HTMLFormElement>("form[data-kit-form]");
    // owner 2026-10-30: no "Print?" unless Settings → Print turns it on; the "Saved" toast stays and the form resets
    if (document.body.dataset.kitPrintPrompt !== "on") { if (form) setTimeout(() => resetForm(form)); return; }   // after htmx finishes the response
    dlg.showModal();

    const handler = (ke: KeyboardEvent) => {
      if (ke.key === "y" || ke.key === "Y" || ke.key === "Enter") {
        ke.preventDefault();
        dlg.close();
        window.open(pendingUrl, "_blank", "noopener");
        if (form) resetForm(form);
        dlg.removeEventListener("keydown", handler);
      } else if (ke.key === "n" || ke.key === "N" || ke.key === "Escape") {
        ke.preventDefault();
        dlg.close();
        if (form) resetForm(form);
        const host = document.getElementById("toast-host");
        if (host) host.replaceChildren();
        dlg.removeEventListener("keydown", handler);
      }
    };
    dlg.addEventListener("keydown", handler);
  });

  document.body.addEventListener("kit:reset", () => {
    const form = document.querySelector<HTMLFormElement>("form[data-kit-form]");
    if (form) resetForm(form);
  });
});
